import Link from "next/link";
import React, { useContext, useState, useTransition } from "react";
import helper from "../../lib/helper";
import ordersModel from "../../models/orders.model";
import useSWR from "swr";
import Pagination from "../../components/Pagination";
import Heading from "../../components/Heading";
import Table from "../../components/Table";
import { useRouter } from "next/router";
import config from "../../lib/config";
import { Button, Dropdown, ToggleButton } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Page = function () {
  const [showLoader, setShowLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const router = useRouter();
  const queryParams = router.query;
  queryParams.order_by = queryParams.order_by ?? "created_at";
  queryParams.order_in = queryParams.order_in ?? "desc";
  queryParams.status = queryParams.status ?? "";

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm();

  const {
    data: ordersData,
    mutate: reloadordersData,
    isLoading,
  } = useSWR(
    queryParams || null,
    () => ordersModel.list(queryParams),
    config.swrConfig
  );

  // Function to send order email
  const sendOrderEmail = async (orderId, subOrderId) => {
    setSendingEmail(true);
    try {
      const response = await ordersModel.email(orderId,subOrderId);
      if (response.data?.success) {
        helper.sweetalert.toast("Email sent successfully!", "success");
      } else {
        helper.sweetalert.toast(response.data?.message || "Failed to send email", "error");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      helper.sweetalert.toast(
        error.response?.data?.message || "An error occurred while sending email",
        "error"
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const ExportOrders = handleSubmit(async (data, event) => {
    const formData = new FormData();

    formData.set("email", data.email);
    formData.set("start_date", helper.date.format(startDate, "DD-MM-YYYY"));
    formData.set("end_date", helper.date.format(endDate, "DD-MM-YYYY"));

    helper.sweetalert.toast(
      `Email will be sent in few minutes to ${data.email}`
    );
    await ordersModel
      .ExportOrder(formData)
      .then((response) => {
        helper.sweetalert.toast(response.data?.message, "success");
        handleCloseModal();
      })
      .catch((error) => {
        console.log(error.response.data?.errors);
        helper.sweetalert.toast(error.response.data?.message, "error");
        _.each(error.response?.data?.errors, function (err, errorKey) {
          setError(errorKey, { type: "required", message: err });
        });
      });
  });

  function statusFilter(status) {
    queryParams.status = status;
    queryParams.page = 1;
    router.push({ query: queryParams });
  }

  const columns = [
    {
      Header: "Order Id",
      accessor: "sub_order_id",
    },
    {
      Header: "Product",
      accessor: "product",
      Cell: ({ row }) => (
        <>
          <b>{row.original?.sku}</b>
          <br />
          <span className="small">{row.original?.title}</span>
        </>
      ),
    },
    {
      Header: "Name",
      accessor: "order.name",
      className: "col",
    },
    {
      Header: "Quantity",
      accessor: "quantity",
    },
    {
      Header: "Amount",
      id: "selling_price",
      Cell: ({ row }) => (
        <>{helper.currency.format(row.original?.selling_price)}</>
      ),
    },
    {
      Header: "City",
      id: "city",
      Cell: ({ row }) => (
        <>
          {row.original?.order?.city}, {row.original?.order?.state}
        </>
      ),
    },
    {
      Header: "AWB",
      id: "awb",
      Cell: ({ row }) => <>{row.original?.awb_number}</>,
    },
    {
      Header: "Payment",
      Cell: ({ row }) => (
        <>
          {row.original?.order?.payment_mode == "cod" ? (
            "COD"
          ) : (
            <>
              {row.original?.order?.payment_mode}
              <br />
              {row.original?.order?.payment_status}
            </>
          )}
        </>
      ),
      className: "col",
    },
    {
      Header: "Status",
      Cell: ({ row }) => <>{row.original?.order?.status}</>,
      className: "col",
    },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ row }) => <>{helper.date.format(row.original?.created_at)}</>,
    },
    {
      className: "col-auto text-end",
      Header: "Actions",
      id: "action",
      Cell: ({ row }) => (
        <>
          <div className="btn-group btn-group-sm">
            <Link
              className="btn btn-primary"
              href={`/orders/${row.original?.id}`}
            >
              <i className="bi bi-eye"></i>
            </Link>
            <Link
              className="btn btn-danger"
              target="_blank"
              href={`${row.original?.invoice}`}
              title="invoice"
            >
              <i className="bi bi-link"></i>
            </Link>
            <Dropdown className="btn-group btn-group-sm">
              <Dropdown.Toggle size="sm" variant="outline-primary">
                <i className="bi bi-three-dots-vertical"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item 
                  onClick={() => sendOrderEmail(row.original?.order?.id, row.original?.sub_order_id)}
                  disabled={sendingEmail}
                  
                >
                 
                  <i className="bi bi-envelope"></i> 
                  {sendingEmail ? "Sending..." : " Email "}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <Heading title="Orders" showLoader={isLoading || showLoader}></Heading>
      <div className="container-fluid">
        <div className="btn-group m-1 overflow-scroll w-100 text-nowrap">
          <ToggleButton
            key="btn-all"
            id={`radio-btn-all}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == ""}
            onChange={(e) => {
              statusFilter("");
            }}
          >
            All
          </ToggleButton>
          <ToggleButton
            key="btn-new"
            id={`radio-new}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "new"}
            onChange={(e) => {
              statusFilter("new");
            }}
          >
            New
          </ToggleButton>
          <ToggleButton
            key="btn-booked"
            id={`radio-booked}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "booked"}
            onChange={(e) => {
              statusFilter("booked");
            }}
          >
            Booked
          </ToggleButton>
          <ToggleButton
            key="btn-packed"
            id={`radio-packed}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "packed"}
            onChange={(e) => {
              statusFilter("packed");
            }}
          >
            Packed
          </ToggleButton>
          <ToggleButton
            key="btn-intransit"
            id={`radio-intransit}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "intransit"}
            onChange={(e) => {
              statusFilter("intransit");
            }}
          >
            In Transit
          </ToggleButton>
          <ToggleButton
            key="btn-delivered"
            id={`radio-delivered}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "delivered"}
            onChange={(e) => {
              statusFilter("delivered");
            }}
          >
            Delivered
          </ToggleButton>
          <ToggleButton
            key="btn-cancelled"
            id={`radio-cancelled}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "cancel"}
            onChange={(e) => {
              statusFilter("cancel");
            }}
          >
            Cancel
          </ToggleButton>
          <ToggleButton
            key="btn-return"
            id={`radio-return}`}
            type="radio"
            variant="outline-secondary"
            name="radio"
            value=""
            checked={queryParams.status == "return"}
            onChange={(e) => {
              statusFilter("return");
            }}
          >
            Return
          </ToggleButton>
        </div>
        <div className="card">
          <div className="card-header">
            Orders List
            <Button className="btn btn-sm float-end" onClick={handleShowModal}>
              Export data
            </Button>
          </div>
          <Modal show={showModal} onHide={handleCloseModal}>
            <Modal.Header closeButton>
              <Modal.Title>Enter Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form className="form-control">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className={`form-control ${errors?.email && "is-invalid"}`}
                  {...register("email", {
                    required: "Email is required",
                  })}
                />
                <div className="invalid-feedback">
                  {errors.email && errors.email.message}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Start-date</label>
                    <DatePicker
                      selected={startDate}
                      className="form-control"
                      dateFormat="dd-MM-YYYY"
                      onChange={(date) => setStartDate(date)}
                      name="start_date"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">End-date</label>
                    <DatePicker
                      selected={endDate}
                      className="form-control"
                      dateFormat="dd-MM-YYYY"
                      onChange={(date) => setEndDate(date)}
                      name="end_date"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary float-end mt-4"
                  onClick={ExportOrders}
                >
                  Generate report
                </button>
              </form>
            </Modal.Body>
          </Modal>
          <div className="table-responsive">
            <Table columns={columns} data={ordersData?.data || []} />
          </div>
          <div className="card-footer table-responsive">
            <Pagination meta={ordersData?.meta} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;